import { useEffect } from "react";
import logo from "../../assets/plan2benefit_logo.png";

const NotFound = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const timeoutId = window.setTimeout(() => {
      window.location.href = "/";
    }, 5000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <main className="relative min-h-[100dvh] overflow-hidden bg-white text-gray-900">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 58% 48% at 78% 20%, rgba(237,233,254,0.95) 0%, transparent 72%),
            radial-gradient(ellipse 38% 34% at 12% 82%, rgba(224,231,255,0.9) 0%, transparent 62%)
          `,
        }}
      />

      <a
        href="/"
        className="absolute top-4 left-4 sm:top-6 sm:left-7 z-20 flex items-center"
      >
        <img
          src={logo}
          alt="Plan2Benefit Logo"
          width={44}
          height={44}
          className="size-9 md:size-11 rounded-lg"
        />
      </a>

      <div className="relative z-10 mx-auto flex min-h-[100dvh] max-w-[1160px] items-center px-4 pb-12 pt-24 sm:px-7 lg:py-24">
        <div className="grid w-full items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-purple-700 shadow-sm backdrop-blur sm:text-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600" />
              Page not found
            </div>

            <h1 className="mt-6 font-montserrat text-[2.35rem] font-bold leading-[1.15] text-gray-900 sm:text-5xl lg:text-[3.6rem] lg:leading-tight">
              This page missed
              <br className="hidden sm:block" /> your 2026 wishlist.
              <span className="mt-4 block bg-gradient-to-r from-purple-600 via-indigo-600 to-violet-600 bg-clip-text text-xl italic text-transparent sm:mt-6 sm:text-2xl lg:text-3xl">
                We&apos;ll send you back to the right savings journey in 5
                seconds.
              </span>
            </h1>

            <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-gray-500 sm:text-base md:text-lg lg:mx-0">
              The URL may be outdated, mistyped, or no longer available. You
              can head back home right away or jump to a page that still helps
              people understand Plan2Benefit.
            </p>

            <div className="mt-8 flex flex-wrap justify-center gap-3 lg:justify-start">
              <a
                href="/"
                className="rounded-2xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] sm:text-base"
              >
                Go to home
              </a>
              <a
                href="/aboutUs/"
                className="rounded-2xl border-2 border-gray-200 bg-white px-6 py-3.5 text-sm font-bold text-gray-800 shadow-sm transition-all duration-300 hover:border-purple-300 hover:text-purple-700 sm:text-base"
              >
                About Plan2Benefit
              </a>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-purple-200/50 via-white to-indigo-200/60 blur-3xl" />
            <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 p-6 shadow-[0_24px_80px_rgba(99,102,241,0.18)] backdrop-blur xl:p-8">
              <div className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-purple-600 to-fuchsia-600 px-5 py-4 text-white shadow-lg">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-white/75">
                    Plan2Benefit
                  </p>
                  <p className="mt-1 text-xl font-bold sm:text-2xl">
                    Redirecting safely
                  </p>
                </div>
                <div className="rounded-2xl bg-white/15 px-4 py-3 text-right">
                  <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-white/75">
                    Timer
                  </p>
                  <p className="text-2xl font-black">5s</p>
                </div>
              </div>

              <div className="mt-5 grid gap-4">
                <div className="rounded-2xl border border-purple-100 bg-purple-50/70 p-5">
                  <p className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-purple-500">
                    Why you&apos;re here
                  </p>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    The page doesn&apos;t match any current Plan2Benefit route.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-gray-600">
                    Instead of dropping you on a broken link, we show a clean
                    fallback and move you to the homepage automatically.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-gray-400">
                      Best next stop
                    </p>
                    <p className="mt-2 text-lg font-bold text-gray-900">
                      Start from home
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Re-enter the main Plan2Benefit journey from the homepage.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50 p-5">
                    <p className="text-[0.7rem] font-bold uppercase tracking-[0.2em] text-gray-400">
                      Need help?
                    </p>
                    <p className="mt-2 text-lg font-bold text-gray-900">
                      Contact support
                    </p>
                    <p className="mt-2 text-sm text-gray-600">
                      Reach us if this URL came from an external campaign or an
                      older link.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default NotFound;
